import * as React from "react";
import { api } from "@/lib/api";

/**
 * CustomFieldsBlock — renders inputs for every custom-field definition
 * on an entity (currently `bookings` and `hr`). Fetches definitions once
 * per (companyId, entity). Values live in the parent form state under
 * `values[fieldKey]`.
 *
 * Props:
 *   companyId, entity: "bookings" | "hr"
 *   values: { [key]: value }
 *   onChange: (nextValues) => void
 *   disabled?: boolean
 *   testidPrefix?: string
 *
 * Renders `null` if the company has no defs (never a blank rectangle).
 */
export default function CustomFieldsBlock({
  companyId, entity, values = {}, onChange, disabled = false, testidPrefix = "cf",
}) {
  const [defs, setDefs] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/custom-fields/${entity}`);
        if (!cancelled) setDefs(data.fields || []);
      } catch {
        if (!cancelled) setDefs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, entity]);

  if (!defs) return null;
  if (defs.length === 0) return null;

  function setValue(key, v) {
    onChange({ ...values, [key]: v });
  }

  return (
    <div className="rounded-md border border-brand-rule p-3 space-y-2" data-testid={`${testidPrefix}-block`}>
      <div className="micro-label">Custom fields</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {defs.map((f) => (
          <div key={f.key} className="flex flex-col gap-1">
            <label className="text-[11px] text-brand-muted flex items-center gap-1">
              {f.label}
              {f.required && <span className="text-brand-coral text-[10px]">*</span>}
            </label>
            {f.type === "text" && (
              <input
                type="text" className="field-input"
                value={values[f.key] || ""}
                disabled={disabled}
                onChange={(e) => setValue(f.key, e.target.value)}
                data-testid={`${testidPrefix}-${f.key}`}
              />
            )}
            {f.type === "number" && (
              <input
                type="number" className="field-input mono-num"
                value={values[f.key] ?? ""}
                disabled={disabled}
                onChange={(e) => setValue(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                data-testid={`${testidPrefix}-${f.key}`}
              />
            )}
            {f.type === "date" && (
              <input
                type="date" className="field-input mono-num"
                value={values[f.key] || ""}
                disabled={disabled}
                onChange={(e) => setValue(f.key, e.target.value)}
                data-testid={`${testidPrefix}-${f.key}`}
              />
            )}
            {f.type === "select" && (
              <select
                className="field-input"
                value={values[f.key] || ""}
                disabled={disabled}
                onChange={(e) => setValue(f.key, e.target.value)}
                data-testid={`${testidPrefix}-${f.key}`}
              >
                <option value="">—</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
